import { Box, Text, VStack } from "@chakra-ui/react";
import FadingImage from "../common/FadingImage";
import { useNavigate } from "react-router-dom";
import { Calendar } from "lucide-react";
import { motion, type Variants } from "framer-motion";
import { NavbarCTAButton } from "../Button";

const MotionBox = motion.create(Box);
const MotionText = motion.create(Text);

const ease = [0.22, 1, 0.36, 1] as [number, number, number, number];

const container: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.18, delayChildren: 0.15 },
  },
};

const itemFadeUp: Variants = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease } },
};

export default function CTASection() {
  const navigate = useNavigate();

  return (
    <Box w="100vw" position="relative" left="50%" ml="-50vw" overflow="hidden">
      <Box
        position="relative"
        minH={{ base: "16rem", tablet: "20rem", desktop: "24rem" }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        py={{ base: "2rem", tablet: "3rem", desktop: "4rem" }}
      >
        {/* Background image */}
        <FadingImage
          position="absolute"
          inset="0"
          w="100%"
          h="100%"
          src="/home/carou1.webp"
          alt=""
          objectFit="cover"
          loading="lazy"
          skeletonStartColor="gray.800"
          skeletonEndColor="gray.700"
          objectPosition={{
            base: "center center",
            tablet: "center 60%",
            desktop: "center 67%",
          }}
        />

        {/* Dark overlay */}
        <Box
          position="absolute"
          inset="0"
          bg="linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.55) 40%, rgba(0,0,0,0.7) 100%)"
        />

        {/* Subtle green tint */}
        <Box
          position="absolute"
          inset="0"
          bg="linear-gradient(90deg, rgba(16,70,45,0.18) 0%, rgba(16,70,45,0.05) 45%, rgba(16,70,45,0.18) 100%)"
        />

        {/* Content */}
        <MotionBox
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          position="relative"
          zIndex={2}
          px={{ base: "1.25rem", tablet: "2rem", desktop: "3rem" }}
          textAlign="center"
          maxW="64rem"
          mx="auto"
        >
          <VStack gap={{ base: "1rem", tablet: "1.25rem", desktop: "1.4rem" }}>
            <MotionText
              variants={itemFadeUp}
              color="white"
              fontSize={{ base: "1.9rem", tablet: "2.5rem", desktop: "3.3rem" }}
              fontWeight="800"
              lineHeight={{ base: "1.15", desktop: "1.08" }}
              letterSpacing="-0.03em"
              textShadow="0 10px 30px rgba(0,0,0,0.28)"
              maxW="48rem"
            >
              Your cleanest home is one booking away.
            </MotionText>

            <MotionText
              variants={itemFadeUp}
              color="rgba(255,255,255,0.92)"
              fontSize={{
                base: "0.98rem",
                tablet: "1.05rem",
                desktop: "1.1rem",
              }}
              lineHeight="1.8"
              maxW="38rem"
              mx="auto"
              textShadow="0 6px 20px rgba(0,0,0,0.24)"
            >
              Match with a trusted cleaner near you. Book in under 2 minutes — no calls, no faff.
            </MotionText>

            <MotionBox variants={itemFadeUp}>
              <NavbarCTAButton
                scrolled={false}
                onClick={() => navigate("/dashboard")}
                icon={<Calendar size={16} />}
                mt="0.4rem"
                h={{ base: "3rem", tablet: "3.25rem", desktop: "3.5rem" }}
                px={{ base: "1.7rem", tablet: "2rem", desktop: "2.3rem" }}
                fontSize={{ base: "0.95rem", desktop: "1rem" }}
                fontWeight="800"
                letterSpacing="0.02em"
              >
                Book Now
              </NavbarCTAButton>
            </MotionBox>
          </VStack>
        </MotionBox>
      </Box>
    </Box>
  );
}
